import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import TimesheetDetailPage from "./pages/TimesheetDetailPage";
import CustomTimesheetEntryPage from "./pages/CustomTimesheetEntryPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import FinancialPage from "./pages/FinancialPage";
import ClientsPage from "./pages/ClientsPage";
import JobsPage from "./pages/JobsPage";
import UsersPage from "./pages/UsersPage";
import InvoicesPage from "./pages/InvoicesPage";
import SettingsPage from "./pages/SettingsPage";
import ReportsPage from "./pages/ReportsPage";
import SubcontractorPaymentsPage from "./pages/SubcontractorPaymentsPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/timesheets"
        element={
          <ProtectedRoute>
            <TimesheetsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/timesheets/:id"
        element={
          <ProtectedRoute>
            <TimesheetDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/timesheet/new"
        element={
          <ProtectedRoute>
            <TimesheetDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/custom-entry/new"
        element={
          <ProtectedRoute adminOnly>
            <CustomTimesheetEntryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/custom-entry/:id"
        element={
          <ProtectedRoute adminOnly>
            <CustomTimesheetEntryPage />
          </ProtectedRoute>
        }
      />

      {/* Admin only routes */}
      <Route
        path="/approvals"
        element={
          <ProtectedRoute adminOnly>
            <ApprovalsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/financial"
        element={
          <ProtectedRoute adminOnly>
            <FinancialPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <ProtectedRoute adminOnly>
            <ClientsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/jobs"
        element={
          <ProtectedRoute adminOnly>
            <JobsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={
          <ProtectedRoute adminOnly>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute adminOnly>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/payments"
        element={
          <ProtectedRoute adminOnly>
            <SubcontractorPaymentsPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
